import { inject, InjectionToken } from '@angular/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import {
  ActionButton,
  CompanyCampaignsCardComponent,
  CompanyCustomColumns,
  CompanyCustomFilterChipComponent,
  CompanyCustomFilterInputComponent,
  CompanyService,
  ContactCampaignsCardComponent,
  ContactCustomColumns,
  CRM_ACCESS_SERVICE_TOKEN,
  CrmDependencies,
  CrmFieldService,
  CRMRowObject,
  CRMSelectAllOptions,
  DueDateCellComponent,
  MultiRowAction,
  ParentChildCompanyAssociationsCardComponent,
  PlatformExtensionFieldIds,
  ProfileCard,
  SelectAllAction,
  SidePanelContentService,
  SingleRowAction,
  StandardExternalIds,
  StandardIds,
  SystemFieldIds,
  TaskCustomColumns,
  UserCellComponent,
  UserFilterChipComponent,
  UserFilterInputComponent,
} from '@galaxy/crm/static';
import { FeatureFlagService, PartnerService, PartnerSubscriptionService, WhitelabelService } from '@galaxy/partner';
import { WhitelabelTranslationService } from '@galaxy/snapshot';
import { TranslateService } from '@ngx-translate/core';
import { Row } from '@vendasta/galaxy/table';
import { combineLatest, firstValueFrom, Observable, of, take } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { FeatureFlags } from '../core/features';
import { SnapshotService } from '../core/snapshot.service';
import { AssociatedAccountComponent } from './company-details/association-panel/associated-account/associated-account.component';
import { OpportunitiesComponent } from './company-details/association-panel/opportunities/opportunities.component';
import { ReportsComponent } from './company-details/association-panel/reports/reports.component';
import { SalesOrdersComponent } from './company-details/association-panel/sales-orders/sales-orders.component';
import {
  SnapshotSidepanelComponentInputs,
  SnapshotSidepanelInputs,
  SnapshotSidepanelService,
  SnapshotSidepanelServiceInputs,
} from './company-table/snapshot-sidepanel/snapshot-sidepanel.service';
import { AdditionalSalespeopleCellComponent } from './custom-cells/additional-salespeople/additional-salespeople.component';
import { SalespersonCellComponent } from './custom-cells/salesperson/salesperson.component';
import { AdditionalSalespeopleFilterInputComponent } from './custom-filters/additional-salespeople/additional-salespeople-filter-input.component';
import { SalespersonFilterChipComponent } from './custom-filters/salesperson/salesperson-filter-chip.component';
import { SalespersonFilterInputComponent } from './custom-filters/salesperson/salesperson-filter-input.component';
import { PccCompanyToContactAssociationsCardComponent } from './profile-cards/pcc-company-to-contact-associations-card/pcc-company-to-contact-associations-card.component';
import { PccContactToCompanyAssociationsCardComponent } from './profile-cards/pcc-contact-to-company-associations-card/pcc-contact-to-company-associations-card.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { CrmCampaignContactsDialogService } from '@galaxy/campaign/shared';
import { SenderType } from '@vendasta/campaigns';
import { SSOService } from '@vendasta/sso';
import { BusinessCenterService } from '../core/business-center.service';
import { AutomationsComponent } from './company-details/association-panel/automations/automations.component';
import { PartnerInfoComponent } from './company-details/association-panel/partner-info/partner-info.component';
import { ActionButtonComponent } from './company-details/open-business-app/open-business-app.component';
import { AdditionalSalespeopleFormInputComponent } from './custom-form-inputs/additional-salespeople/additional-salespeople-form-input.component';
import { SalespersonFormInputComponent } from './custom-form-inputs/salesperson/salesperson-form-input.component';
import { PartnerCRMOpportunityService } from './opportunity.service';
import { CRMUserService } from './user.service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InboxNavigationService, SubjectParticipant } from '@galaxy/conversation/core';
import { Salesperson } from '@galaxy/types';
import { ConversationChannel, GlobalParticipantType } from '@vendasta/conversation';
import { SalespersonService } from '@vendasta/sales';
import { Opportunity as ApiOpportunity } from '@vendasta/sales-opportunities';
import {
  MARKET_SELECTOR_SERVICE_TOKEN,
  OpportunityDetailsComponent,
  OpportunityService,
  OrdersUrl,
  OrdersUrlFunction,
  SalesUIOpportunityConfig,
  UserPipelines,
  UserPipelinesServiceConfig,
} from '@vendasta/sales-ui';
import { SalespeopleService } from '../manage_accounts/salespeople.service';
import { BusinessInfoService } from './business-info.service';
import { FeatureAccessService } from './feature-access.service';
import { Feature } from 'marketplace-ui';
import { Router } from '@angular/router';
import {
  CrmFulfillmentProjectsAssociationCardComponent,
  CrmMarketplaceProductsAssociationCardComponent,
} from '@galaxy/crm/dynamic';
import { MarketFilterChipComponent, MarketFilterInputComponent } from './custom-filters/market';
import { MarketsService } from '../core/markets/markets.service';
import { AUTOMATION_IS_USER_SUPERADMIN$ } from '@galaxy/automata/shared';
import {
  AutomationActionsService,
  AutomationMultiRowAction,
  AutomationSingleRowAction,
} from '@galaxy/crm/integrations';
import { ComponentPortal } from '@angular/cdk/portal';
interface FactoryServices {
  partnerService: PartnerService;
  whitelabelTranslationService: WhitelabelTranslationService;
  snapshotService: SnapshotService;
  snapshotSidepanelService: SnapshotSidepanelService;
  crmFieldService: CrmFieldService;
  businessCenterService: BusinessCenterService;
  ssoService: SSOService;
  whitelabelService: WhitelabelService;
  translationService: TranslateService;
  companyService: CompanyService;
  router: Router;
  canStartManualAutomation$: Observable<boolean>;
  automationsActionsService: AutomationActionsService;
  opportunitiesService: OpportunityService;
  content: SidePanelContentService;
}

interface MultiActionServices {
  campaignService: CrmCampaignContactsDialogService;
  translationSvc: TranslateService;
  partnerSvc: PartnerService;
  featureFlagService: FeatureFlagService;
  partnerSubscriptionService: PartnerSubscriptionService;
  productAnalyticsService: ProductAnalyticsService;
}

function buildCompanyProfileCards$(
  partnerId$: Observable<string>,
  companyCompanyAssociationFlag$: Observable<boolean>,
): Observable<ProfileCard[]> {
  return combineLatest([partnerId$, companyCompanyAssociationFlag$]).pipe(
    map(([partnerId, companyCompanyAssociationFlag]) => {
      const profileCards: ProfileCard[] = [];
      if (partnerId === 'VMF') {
        profileCards.push({ component: PartnerInfoComponent });
      }
      profileCards.push({ component: PccCompanyToContactAssociationsCardComponent });
      profileCards.push({ component: AssociatedAccountComponent });
      profileCards.push({ component: CrmMarketplaceProductsAssociationCardComponent });
      profileCards.push({ component: OpportunitiesComponent });
      profileCards.push({ component: AutomationsComponent });
      profileCards.push({ component: ReportsComponent });
      profileCards.push({ component: SalesOrdersComponent });
      profileCards.push({ component: CompanyCampaignsCardComponent });
      profileCards.push({ component: CrmFulfillmentProjectsAssociationCardComponent });
      if (companyCompanyAssociationFlag) {
        profileCards.push({ component: ParentChildCompanyAssociationsCardComponent });
      }
      return profileCards;
    }),
  );
}

function buildContactProfileCards$(): Observable<ProfileCard[]> {
  const profileCards = [
    { component: PccContactToCompanyAssociationsCardComponent },
    { component: ContactCampaignsCardComponent },
  ] as ProfileCard[];
  return of(profileCards);
}

export const CrmInjectionToken = new InjectionToken<CrmDependencies>('dependencies given to crm library', {
  factory: (): CrmDependencies => {
    const inboxNavigationService = inject(InboxNavigationService);
    const partnerService = inject(PartnerService);
    const configService = inject(AppConfigService);
    const marketsService = inject(MarketsService);
    const featureFlagService = inject(FeatureFlagService);
    const whitelabelTranslationService = inject(WhitelabelTranslationService);
    const snapshotService = inject(SnapshotService);
    const snapshotSidepanelService = inject(SnapshotSidepanelService);
    const crmCampaignContactsDialogService = inject(CrmCampaignContactsDialogService);
    const translationService = inject(TranslateService);
    const whitelabelService = inject(WhitelabelService);
    const crmFieldService = inject(CrmFieldService);
    const businessCenterService = inject(BusinessCenterService);
    const ssoService = inject(SSOService);
    const userService = inject(CRMUserService);
    const accessService = inject(CRM_ACCESS_SERVICE_TOKEN);
    const opportunityService = inject(PartnerCRMOpportunityService);
    const featureAccessService = inject(FeatureAccessService);
    const companyService = inject(CompanyService);
    const router = inject(Router);
    const salespersonService = inject(SalespersonService);
    const isUserSuperAdmin$ = inject(AUTOMATION_IS_USER_SUPERADMIN$);
    const automationsActionsService = inject(AutomationActionsService);
    const canStartManualAutomation$ = combineLatest([
      isUserSuperAdmin$,
      featureAccessService.hasAccessToFeature(Feature.automations),
    ]).pipe(map(([isSuperAdmin, hasAccess]) => !isSuperAdmin && hasAccess));
    const content = inject(SidePanelContentService);
    const opportunitiesService = inject(OpportunityService);
    const factoryServices: FactoryServices = {
      partnerService,
      whitelabelTranslationService,
      snapshotService,
      snapshotSidepanelService,
      ssoService,
      whitelabelService,
      crmFieldService,
      businessCenterService,
      translationService,
      companyService,
      router,
      canStartManualAutomation$,
      automationsActionsService,
      opportunitiesService,
      content,
    };
    const multiActionServices: MultiActionServices = {
      campaignService: crmCampaignContactsDialogService,
      translationSvc: translationService,
      partnerSvc: partnerService,
      featureFlagService: featureFlagService,
      partnerSubscriptionService: inject(PartnerSubscriptionService),
      productAnalyticsService: inject(ProductAnalyticsService),
    };
    const translate = inject(TranslateService);
    const partnerId$ = partnerService.getPartnerId();
    const companyCompanyAssociationFlag$ = partnerId$.pipe(
      switchMap((partnerID) =>
        featureFlagService.batchGetStatus(partnerID, '', [FeatureFlags.CRM_COMPANY_COMPANY_ASSOCIATION]),
      ),
      map((featureFlags) => featureFlags[FeatureFlags.CRM_COMPANY_COMPANY_ASSOCIATION]),
    );
    const contactPhoneFieldID = crmFieldService.getFieldId(StandardExternalIds.PhoneNumber);
    const emailFieldID = crmFieldService.getFieldId(StandardExternalIds.Email);
    const tagsFieldID = crmFieldService.getFieldId(StandardExternalIds.Tags);

    const currentUserId$ = configService.config$.pipe(
      map((config) => config.unifiedUserId),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const salespersonId$ = combineLatest([partnerId$, currentUserId$]).pipe(
      switchMap(([partnerId, userId]) => salespersonService.getSalespersonByUserId(partnerId, userId)),
      map((salesperson) => salesperson.salespersonId),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError((_) => of('')),
    );

    return {
      appID: 'partner-center-client',
      routePrefix$: of('/crm'),
      namespace$: partnerId$,
      parentNamespace$: of(''),
      viewProfileFlag$: of(true),
      viewProspectorFlag$: partnerId$.pipe(
        switchMap((partnerId) =>
          featureFlagService.batchGetStatus(partnerId, 'default', [FeatureFlags.CRM_LEAD_PROSPECTOR]),
        ),
        map((featureFlags) => featureFlags[FeatureFlags.CRM_LEAD_PROSPECTOR]),
      ),
      currentUserId$: currentUserId$,
      contact: {
        selectAllTableActions$: buildSelectAllConatctTableActions(multiActionServices),
        multiRowTableActions$: buildMultiRowContactTableActions(multiActionServices, factoryServices),
        singleRowTableActions$: buildSingleRowContactTableActions(factoryServices),
        profileCards$: buildContactProfileCards$(),
        filterInputOverrides$: of([
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.PRIMARY_COMPANY'),
            fieldId: StandardIds.ContactPrimaryCompanyID,
            filterInput: CompanyCustomFilterInputComponent,
            filterChip: CompanyCustomFilterChipComponent,
          },
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.SALESPERSON'),
            fieldId: StandardIds.ContactPrimarySalespersonID,
            filterInput: SalespersonFilterInputComponent,
            filterChip: SalespersonFilterChipComponent,
          },
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.ADDITIONAL_SALESPEOPLE'),
            fieldId: StandardIds.ContactAdditionalSalespersonIDs,
            filterInput: AdditionalSalespeopleFilterInputComponent,
            filterChip: SalespersonFilterChipComponent,
          },
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.GROUP'),
            fieldId: SystemFieldIds.ContactGroupID,
            filterInput: MarketFilterInputComponent,
            filterChip: MarketFilterChipComponent,
          },
        ]),
        tableCustomCells: [
          {
            fieldIds: [StandardIds.ContactPrimarySalespersonID],
            columnDefinition: {
              id: 'customSalespersonColumn',
              title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.SALESPERSON'),
            },
            customCellComponent: SalespersonCellComponent,
          },
          {
            fieldIds: [StandardIds.ContactAdditionalSalespersonIDs],
            columnDefinition: {
              id: 'customAdditionalSalespeopleColumn',
              title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.ADDITIONAL_SALESPEOPLE'),
            },
            customCellComponent: AdditionalSalespeopleCellComponent,
          },
        ],
        formCustomInputs: [
          {
            fieldId: StandardIds.ContactPrimarySalespersonID,
            component: SalespersonFormInputComponent,
          },
          {
            fieldId: StandardIds.ContactAdditionalSalespersonIDs,
            component: AdditionalSalespeopleFormInputComponent,
          },
        ],
        formDefaultOnCreateInputValues$: combineLatest([
          salespersonId$,
          configService.config$,
          marketsService.currentMarket$,
        ]).pipe(
          map(([salespersonId, appConfig, currentMarket]) => {
            const defaults = [
              { fieldId: StandardIds.ContactLifecycleStage, value: 'Lead' },
              { fieldId: SystemFieldIds.ContactOwnerID, value: appConfig.unifiedUserId },
              { fieldId: StandardIds.ContactSourceName, value: 'CRM UI' },
              { fieldId: StandardIds.ContactRecordSourceDrill1, value: window.location.href },
              { fieldId: StandardIds.ContactOriginalSource, value: 'CRM UI' },
              { fieldId: StandardIds.ContactOriginalSourceDrill1, value: window.location.href },
            ];
            if (!!currentMarket && !!currentMarket.market_id) {
              defaults.push({
                fieldId: SystemFieldIds.ContactGroupID,
                value: currentMarket.market_id,
              });
            }
            if (salespersonId) {
              defaults.push({
                fieldId: StandardIds.ContactPrimarySalespersonID,
                value: salespersonId,
              });
            }
            return defaults;
          }),
        ),
        baseColumnIds: [
          ContactCustomColumns.FullName,
          ContactCustomColumns.PrimaryCompanyName,
          contactPhoneFieldID,
          emailFieldID,
          tagsFieldID,
          StandardIds.ContactSourceName,
          SystemFieldIds.ContactCreated,
        ],
      },
      customActions: {
        navigateToFulfillmentProjects: navigateToFulfillmentProjects(factoryServices),
        openOpportunitiesDetails: openOpportunityDetails(factoryServices),
      },
      company: {
        singleRowTableActions$: buildSingleRowCompanyTableActions(factoryServices),
        multiRowTableActions$: buildMultiRowCompanyTableActions(factoryServices),
        actionButtons$: of([{ component: ActionButtonComponent }] as ActionButton[]),
        profileCards$: buildCompanyProfileCards$(partnerId$, companyCompanyAssociationFlag$),
        filterInputOverrides$: of([
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.PARENT_COMPANY'),
            fieldId: StandardIds.CompanyParentCompanyID,
            filterInput: CompanyCustomFilterInputComponent,
            filterChip: CompanyCustomFilterChipComponent,
          },
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.SALESPERSON'),
            fieldId: StandardIds.CompanyPrimarySalespersonID,
            filterInput: SalespersonFilterInputComponent,
            filterChip: SalespersonFilterChipComponent,
          },
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.ADDITIONAL_SALESPEOPLE'),
            fieldId: StandardIds.CompanyAdditionalSalespersonIDs,
            filterInput: AdditionalSalespeopleFilterInputComponent,
            filterChip: SalespersonFilterChipComponent,
          },
          {
            title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.GROUP'),
            fieldId: SystemFieldIds.CompanyGroupID,
            filterInput: MarketFilterInputComponent,
            filterChip: MarketFilterChipComponent,
          },
        ]),
        tableCustomCells: [
          {
            fieldIds: [StandardIds.CompanyPrimarySalespersonID],
            columnDefinition: {
              id: 'customSalespersonColumn',
              title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.SALESPERSON'),
            },
            customCellComponent: SalespersonCellComponent,
          },
          {
            fieldIds: [StandardIds.CompanyAdditionalSalespersonIDs],
            columnDefinition: {
              id: 'customAdditionalSalespeopleColumn',
              title: translate.instant('CRM.TABLE.CUSTOM_COLUMNS.ADDITIONAL_SALESPEOPLE'),
            },
            customCellComponent: AdditionalSalespeopleCellComponent,
          },
        ],
        formCustomInputs: [
          {
            fieldId: StandardIds.CompanyPrimarySalespersonID,
            component: SalespersonFormInputComponent,
          },
          {
            fieldId: StandardIds.CompanyAdditionalSalespersonIDs,
            component: AdditionalSalespeopleFormInputComponent,
          },
        ],
        formDefaultOnCreateInputValues$: combineLatest([
          salespersonId$,
          configService.config$,
          marketsService.currentMarket$,
        ]).pipe(
          map(([salespersonId, appConfig, currentMarket]) => {
            const defaults = [
              { fieldId: StandardIds.CompanyLifecycleStage, value: 'Lead' },
              { fieldId: SystemFieldIds.CompanyOwnerID, value: appConfig.unifiedUserId },
              { fieldId: StandardIds.CompanySourceName, value: 'CRM UI' },
              { fieldId: StandardIds.CompanyRecordSourceDrill1, value: window.location.href },
              { fieldId: StandardIds.CompanyOriginalSource, value: 'CRM UI' },
              { fieldId: StandardIds.CompanyOriginalSourceDrill1, value: window.location.href },
            ];
            if (!!currentMarket && !!currentMarket.market_id) {
              defaults.push({
                fieldId: SystemFieldIds.CompanyGroupID,
                value: currentMarket?.market_id,
              });
            }
            if (salespersonId) {
              defaults.push({
                fieldId: StandardIds.CompanyPrimarySalespersonID,
                value: salespersonId,
              });
            }
            return defaults;
          }),
        ),
        baseColumnIds: [
          StandardIds.CompanyName,
          StandardIds.CompanyPhoneNumber,
          StandardIds.CompanyPhoneNumber,
          StandardExternalIds.Email,
          CompanyCustomColumns.FullAddress,
          'customSalespersonColumn',
          StandardIds.CompanyTags,
        ],
      },
      task: {
        filterInputOverrides$: of([
          {
            title: translate.instant('CRM.TASK.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.ASSIGNED_TO'),
            fieldId: SystemFieldIds.ActivityOwnerID,
            filterInput: UserFilterInputComponent,
            filterChip: UserFilterChipComponent,
          },
        ]),
        tableCustomCells: [
          {
            fieldIds: [SystemFieldIds.ActivityOwnerID],
            columnDefinition: {
              id: TaskCustomColumns.AssignedTo,
              title: translate.instant('CRM.TASK.LIST_OBJECTS_TABLE.CUSTOM_COLUMNS.ASSIGNED_TO'),
            },
            customCellComponent: UserCellComponent,
          },
          {
            fieldIds: [StandardIds.ActivityTaskDueDate, StandardIds.ActivityTaskStatus],
            columnDefinition: {
              id: StandardIds.ActivityTaskDueDate,
            },
            customCellComponent: DueDateCellComponent,
          },
        ],
      },
      showBackToAccountsButton: true,
      services: {
        userService: userService,
        accessService: accessService,
        opportunityService: opportunityService,
      },
      openAccountGroupConversation: async (accountGroupId: string) => {
        const partnerId = await firstValueFrom(partnerId$);
        inboxNavigationService.gotoConversationChannel(
          [
            new SubjectParticipant({
              internalParticipantId: partnerId,
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
            }),
            new SubjectParticipant({
              internalParticipantId: accountGroupId,
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
            }),
          ],
          ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
        );
      },
      hasAccessToFeature$: (featureString: string) => {
        const feature = featureString as Feature;
        return featureAccessService.hasAccessToFeature(feature);
      },
      openRestrictedDialog: (featureId) => {
        featureAccessService.openRestrictedDialog(featureId);
      },
    };
  },
});

function buildSingleRowCompanyTableActions(services: FactoryServices): Observable<SingleRowAction[]> {
  const vbcEntryInfo$ = services.partnerService
    .getPartnerId()
    .pipe(switchMap((partnerId) => services.businessCenterService.getVBCEntryUrl(partnerId)));
  return combineLatest([
    vbcEntryInfo$,
    services.partnerService.getPartnerId(),
    services.canStartManualAutomation$,
  ]).pipe(
    map(([vbcEntryInfo, partnerId, canStartManualAutomation]) => {
      const actions: SingleRowAction[] = [];
      actions.push({
        label: services.whitelabelTranslationService.instant('SNAPSHOT_REPORT.COMMON.SNAPSHOT_NAME'),
        callback: (row: Row) => {
          const sidepanelInputs: SnapshotSidepanelInputs = {
            serviceInputs: {
              accountGroupID: row?.data?.[PlatformExtensionFieldIds.AccountGroupID]?.value as string,
            } as SnapshotSidepanelServiceInputs,
            componentInputs: {
              companyID: row?.data?.[SystemFieldIds.CompanyID]?.value as string,
              partnerID: partnerId,
              trackingCategory: 'list-company-table',
              snapshotName: services.whitelabelTranslationService.instant('SNAPSHOT_REPORT.COMMON.SNAPSHOT_NAME'),
            } as SnapshotSidepanelComponentInputs,
          };
          services.snapshotSidepanelService.openSidepanel(sidepanelInputs);
        },
      });
      actions.push({
        label:
          services.translationService.instant('COMPANY.LIST_OBJECTS_TABLE.ACTIONS.OPEN_BUSINESS_APP') +
          ' ' +
          vbcEntryInfo.name,
        visible: (row: Row) => {
          const accountGroupId = row?.data?.[PlatformExtensionFieldIds.AccountGroupID]?.value as string;
          return !!accountGroupId;
        },
        callback: (row: Row) => {
          services.businessCenterService
            .getVBCEntryUrl(row?.data?.[PlatformExtensionFieldIds.AccountGroupID]?.value as string)
            .pipe(take(1))
            .subscribe((vbcEntryInfo) => {
              window.open(vbcEntryInfo.url, '_blank');
            });
        },
      });
      if (canStartManualAutomation) {
        actions.push(
          AutomationSingleRowAction(
            partnerId,
            services.automationsActionsService,
            'Company',
            services.translationService.instant('COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION'),
          ),
        );
      }
      return actions;
    }),
  );
}

function buildSingleRowContactTableActions(services: FactoryServices): Observable<SingleRowAction[]> {
  return combineLatest([services.canStartManualAutomation$, services.partnerService.getPartnerId()]).pipe(
    map(([canStartManualAutomation, partnerId]) => {
      const actions: SingleRowAction[] = [];
      if (canStartManualAutomation) {
        actions.push(
          AutomationSingleRowAction(
            partnerId,
            services.automationsActionsService,
            'Contact',
            services.translationService.instant('COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION'),
          ),
        );
      }
      return actions;
    }),
  );
}

function buildMultiRowCompanyTableActions(services: FactoryServices): Observable<MultiRowAction[]> {
  return combineLatest([services.canStartManualAutomation$, services.partnerService.getPartnerId()]).pipe(
    map(([canStartManualAutomation, partnerId]) => {
      const actions: MultiRowAction[] = [];
      if (canStartManualAutomation) {
        actions.push(
          AutomationMultiRowAction(
            partnerId,
            services.automationsActionsService,
            'Company',
            services.translationService.instant('COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION'),
          ),
        );
      }
      return actions;
    }),
  );
}

function buildMultiRowContactTableActions(
  MultiActionServices: MultiActionServices,
  services: FactoryServices,
): Observable<MultiRowAction[]> {
  const canAccessMarketingAutomation$ = MultiActionServices.partnerSvc.getPartnerId().pipe(
    switchMap((partnerId) => {
      return MultiActionServices.partnerSubscriptionService.canAccessFeature('marketing-automation', partnerId);
    }),
  );
  return combineLatest([
    canAccessMarketingAutomation$,
    services.canStartManualAutomation$,
    services.partnerService.getPartnerId(),
  ]).pipe(
    map(([canAccessMarketingAutomation, canStartManualAutomation, partnerId]) => {
      const actions: MultiRowAction[] = [];
      if (canAccessMarketingAutomation) {
        actions.push({
          label: MultiActionServices.translationSvc.instant('CONTACT_TABLE_ADD_TO_CAMPAIGN'),
          callback: (rows: Row[]) => {
            MultiActionServices.productAnalyticsService.trackEvent(
              'user-clicked-crm-table-add-multi-contacts',
              'send-campaign-workflow',
              'click',
              null,
            );
            MultiActionServices.campaignService.openCampaignDialog(partnerId, SenderType.SENDER_TYPE_PARTNER, rows);
          },
        });
      }
      if (canStartManualAutomation) {
        actions.push(
          AutomationMultiRowAction(
            partnerId,
            services.automationsActionsService,
            'Contact',
            services.translationService.instant('COMPANY.LIST_OBJECTS_TABLE.ACTIONS.START_AUTOMATION'),
          ),
        );
      }
      return actions;
    }),
  );
}

function buildSelectAllConatctTableActions(MultiActionServices: MultiActionServices): Observable<SelectAllAction[]> {
  return MultiActionServices.partnerSvc.getPartnerId().pipe(
    switchMap((partnerId) => {
      return MultiActionServices.featureFlagService.batchGetStatus(partnerId, '', ['select_all_contacts_option']).pipe(
        switchMap((showSelectAllObjects) => {
          if (showSelectAllObjects['select_all_contacts_option']) {
            return MultiActionServices.partnerSubscriptionService
              .canAccessFeature('marketing-automation', partnerId)
              .pipe(
                map((canAccess) => {
                  if (canAccess) {
                    return [
                      {
                        label: MultiActionServices.translationSvc.instant('CONTACT_TABLE_ADD_TO_CAMPAIGN'),
                        callback: (rows: Row[], selectOptions?: CRMSelectAllOptions) => {
                          MultiActionServices.productAnalyticsService.trackEvent(
                            'user-clicked-crm-table-select-all-contacts',
                            'send-campaign-workflow',
                            'click',
                            null,
                          );
                          MultiActionServices.campaignService.openCampaignDialog(
                            partnerId,
                            SenderType.SENDER_TYPE_PARTNER,
                            rows,
                            selectOptions.filters,
                            selectOptions.search,
                            selectOptions.useSelectAll,
                            selectOptions.totalObjects,
                          );
                        },
                        selectAllVisible: true,
                      },
                    ];
                  } else {
                    return [];
                  }
                }),
              );
          } else {
            return [];
          }
        }),
      );
    }),
  );
}

export function userPipelinesFactory(): UserPipelinesServiceConfig {
  const partnerAAConfigService = inject(AppConfigService);

  const partnerId$ = partnerAAConfigService.config$.pipe(
    map((config) => config.partnerId),
    filter((partnerId) => !!partnerId),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  return <UserPipelinesServiceConfig>{ partnerId$: partnerId$ };
}

export function opportunityServiceConfigFactory(): SalesUIOpportunityConfig {
  const partnerAAConfigService = inject(AppConfigService);
  const userPipelines = inject(UserPipelines);
  const salespersonService = inject(SalespersonService);
  const featureFlagService = inject(FeatureFlagService);
  const salespeopleService = inject(SalespeopleService);
  const businessInfoService = inject(BusinessInfoService);
  const partnerConfigService = inject(WhitelabelService);
  const marketSelectorService = inject(MARKET_SELECTOR_SERVICE_TOKEN);

  const partnerId$ = partnerAAConfigService.config$.pipe(
    map((config) => config.partnerId),
    filter((partnerId) => !!partnerId),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  const userId$ = partnerAAConfigService.config$.pipe(
    map((config) => config.unifiedUserId),
    filter((userId) => !!userId),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  const salesperson$ = combineLatest([partnerId$, userId$]).pipe(
    switchMap(([partnerId, userId]) => salespersonService.getSalespersonByUserId(partnerId, userId)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  const salespersonId$ = salesperson$.pipe(
    map((salesperson) => salesperson.salespersonId),
    catchError((_) => of('')),
  );

  const canCreateOrders$ = partnerId$.pipe(
    switchMap((partnerId) => {
      return featureFlagService.batchGetStatus(partnerId, '', [FeatureFlags.ADMINS_CREATE_ORDERS]);
    }),
    map((featureFlagStatus) => featureFlagStatus[FeatureFlags.ADMINS_CREATE_ORDERS]),
    shareReplay(1),
  );

  const getOrdersUrlFn: OrdersUrlFunction = (opportunity: ApiOpportunity): OrdersUrl => {
    return <OrdersUrl>{
      route: `/order-management/${opportunity.accountGroupId}/sales-order/create/${opportunity.opportunityId}`,
    };
  };

  const marketId$ = marketSelectorService.selectedMarketId$;

  const partnerMarket$ = combineLatest([partnerId$, marketId$]).pipe(
    map(([partnerId, marketId]) => ({
      partnerId,
      marketId,
    })),
    distinctUntilChanged(isPartnerMarketSame),
  );

  partnerMarket$.subscribe((pm) => salespeopleService.loadSalespeople(pm.partnerId, pm.marketId, ''));

  const bypassQuantityRestrictionsPartnerIds = ['VMF'];
  const bypassQuantityRestrictions$ = partnerId$.pipe(
    map((partnerId) => bypassQuantityRestrictionsPartnerIds.includes(partnerId)),
  );

  const salesCenterCurrency$ = partnerMarket$.pipe(
    switchMap((pm) => {
      return partnerConfigService.getConfiguration(pm.partnerId, pm.marketId);
    }),
    map((config) => {
      return config?.salesConfiguration?.stDefaultDisplayCurrency || 'USD';
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  const showActivities$ = partnerId$.pipe(
    switchMap((partnerId) => {
      return featureFlagService.batchGetStatus(partnerId, '', [FeatureFlags.OPPORTUNITY_CRM_ACTIVITY]);
    }),
    map((featureFlagStatus) => featureFlagStatus[FeatureFlags.OPPORTUNITY_CRM_ACTIVITY]),
    shareReplay(1),
  );

  const cfg: SalesUIOpportunityConfig = {
    salespersonId$: salespersonId$,
    pipelines$: userPipelines.partnerPipelines$,
    currentPipeline$: userPipelines.getCurrentPipeline(),
    canCreateOrders$: canCreateOrders$,
    getOrdersUrl: getOrdersUrlFn,
    partnerId$: partnerId$,
    salespeople$: salespeopleService.salespeople$.pipe(
      map((salespeople) => Salesperson.convertSalespeopleFromSalesResponse(salespeople)),
    ),
    marketId$: marketId$,
    bypassQuantityRestrictions$: bypassQuantityRestrictions$,
    businessInfoService: businessInfoService,
    hasAccessToAllAccountsInMarket$: of(true),
    defaultCurrency$: salesCenterCurrency$,
    showActivities$,
  };

  return cfg;
}

interface PartnerMarket {
  partnerId: string;
  marketId: string;
}

const isPartnerMarketSame = (prev: PartnerMarket, curr: PartnerMarket): boolean => {
  return prev?.partnerId === curr?.partnerId && prev?.marketId === curr?.marketId;
};

function navigateToFulfillmentProjects(services: FactoryServices): (row: CRMRowObject) => void {
  return (row: CRMRowObject): void => {
    const pid$ = services.partnerService.getPartnerId();
    const crmObject$ = pid$.pipe(
      switchMap((pid) => services.companyService.getMultiObject({ namespace: pid, crmObjectIds: [row.objectId] })),
      map((companies) => companies?.crmObjects?.[0]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    const agid$ = crmObject$.pipe(
      map((o) => o?.fields?.find((f) => f.fieldId === PlatformExtensionFieldIds.AccountGroupID)?.stringValue || ''),
    );
    const name$ = crmObject$.pipe(
      map((o) => o?.fields?.find((f) => f.fieldId === StandardIds.CompanyName)?.stringValue || ''),
    );
    const companyInfo = firstValueFrom(combineLatest([agid$, name$]));
    companyInfo.then(([accountGroupId, name]) => {
      const url = 'task-manager/reporting';
      const queryParams = { 'project-report': JSON.stringify({ account: { accountGroupId, name } }) };
      services.router.navigate([url], { queryParams });
    });
  };
}

function openOpportunityDetails(services: FactoryServices): (opportunityId: string, accountGroupId: string) => void {
  return (opportunityId: string, accountGroupId: string): void => {
    services.opportunitiesService.loadOpportunityAndSetSelected(opportunityId, accountGroupId);
    const c = new ComponentPortal(OpportunityDetailsComponent);
    services.content.setContent(c);
  };
}
